import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ROUTES } from '../../common-files/enums/server-routes';

// (HttpClient handles FormData for the image upload; no Content-Type set here.)

// Operator Admin (CENTRAL) API calls: block-a-day, operator (user) management,
// and per-user permission overrides. CENTRAL-scoped.
@Injectable({ providedIn: 'root' })
export class OperatorAdminService {
  constructor(private http: HttpClient) {}

  // -- block a day --
  blockDay(provisioningDate: string): Observable<any> {
    return this.http.post(ROUTES.OA_BLOCK_DAY, { provisioningDate });
  }

  unblockDay(provisioningDate: string): Observable<any> {
    return this.http.post(ROUTES.OA_UNBLOCK_DAY, { provisioningDate });
  }

  listBlockedDays(): Observable<any> {
    return this.http.get(ROUTES.OA_BLOCKED_DAYS);
  }

  // -- operator (user) management --
  listOperators(): Observable<any> {
    return this.http.get(ROUTES.OA_OPERATORS);
  }

  addOperator(payload: any): Observable<any> {
    return this.http.post(ROUTES.OA_OPERATORS, payload);
  }

  updateOperator(sumId: number, payload: any): Observable<any> {
    return this.http.put(`${ROUTES.OA_OPERATORS}/${sumId}`, payload);
  }

  // the CENTRAL roles the admin may hand out (feeds the add / edit role dropdown).
  listAssignableRoles(): Observable<any> {
    return this.http.get(ROUTES.OA_ASSIGNABLE_ROLES);
  }

  // activity across all operators; params: fromDate, toDate (yyyy-mm-dd), operatorName.
  getActivity(params: { fromDate?: string; toDate?: string; operatorName?: string }): Observable<any> {
    const query: any = {};
    if (params.fromDate) query.fromDate = params.fromDate;
    if (params.toDate) query.toDate = params.toDate;
    if (params.operatorName) query.operatorName = params.operatorName;
    return this.http.get(ROUTES.OA_ACTIVITY, { params: query });
  }

  setOperatorRestricted(sumId: number, restricted: boolean): Observable<any> {
    const action = restricted ? 'restrict' : 'unrestrict';
    return this.http.post(`${ROUTES.OA_OPERATORS}/${sumId}/${action}`, {});
  }

  // -- per-user permission overrides --
  listViewPermissions(): Observable<any> {
    return this.http.get(ROUTES.OA_VIEW_PERMISSIONS);
  }

  listUserPermissions(sumId: number): Observable<any> {
    return this.http.get(`${ROUTES.OA_OPERATORS}/${sumId}/permissions`);
  }

  setUserPermission(sumId: number, payload: any): Observable<any> {
    return this.http.post(`${ROUTES.OA_OPERATORS}/${sumId}/permissions`, payload);
  }

  removeUserPermission(sumId: number, supdId: number): Observable<any> {
    return this.http.delete(`${ROUTES.OA_OPERATORS}/${sumId}/permissions/${supdId}`);
  }

  // -- utsav management --
  listUtsav(): Observable<any> {
    return this.http.get(ROUTES.OA_UTSAV);
  }

  // the active utsav masters (festivals) for the instance form's dropdown
  listUtsavMasters(): Observable<any> {
    return this.http.get(`${ROUTES.OA_UTSAV}-masters`);
  }

  addUtsav(payload: any): Observable<any> {
    return this.http.post(ROUTES.OA_UTSAV, payload);
  }

  updateUtsav(utsavInstanceId: number, payload: any): Observable<any> {
    return this.http.put(`${ROUTES.OA_UTSAV}/${utsavInstanceId}`, payload);
  }

  activateUtsav(utsavInstanceId: number): Observable<any> {
    return this.http.post(`${ROUTES.OA_UTSAV}/${utsavInstanceId}/activate`, {});
  }

  // summary + bookings + allocations for one utsav, in one call
  getUtsavReport(utsavInstanceId: number): Observable<any> {
    return this.http.get(`${ROUTES.OA_UTSAV}/${utsavInstanceId}/report`);
  }

  uploadUtsavImage(file: File): Observable<any> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post(ROUTES.OA_UTSAV_IMAGE, form);
  }

  // -- admin requisition queue --
  listPending(): Observable<any> {
    return this.http.get(ROUTES.OA_REQ_PENDING);
  }

  listConfirmedToCancel(): Observable<any> {
    return this.http.get(ROUTES.OA_REQ_CONFIRMED_CANCEL);
  }

  openRequisition(bookingId: number): Observable<any> {
    return this.http.post(`${ROUTES.OA_REQUISITION}/${bookingId}/open`, {});
  }

  bulkRollbackAll(): Observable<any> {
    return this.http.post(ROUTES.OA_REQ_BULK_ROLLBACK, {});
  }
}
